import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useAction } from "convex/react";
import { useState } from "react";
import { toast } from "sonner";

import { stripeTerminalUpdateReader } from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Spinner } from "~/components/ui/spinner";
import { logger } from "~/lib/logger";
import { requiredSearchString } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/terminals/readers/$readerId/edit"
)({
  component: EditReaderModal,
  validateSearch: (search: Record<string, unknown>) => ({
    label: requiredSearchString(search.label),
  }),
});

function EditReaderModal() {
  const navigate = useNavigate();
  const { readerId } = useParams({
    from: "/_authenticated/settings/terminals/readers/$readerId/edit",
  });
  const { label: initialLabel } = useSearch({
    from: "/_authenticated/settings/terminals/readers/$readerId/edit",
  });

  const updateReader = useAction(stripeTerminalUpdateReader);

  const [label, setLabel] = useState(initialLabel);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    void navigate({ to: "/settings/terminals" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await updateReader({
        readerId,
        label: label || undefined,
      });
      ProductEvents.readerUpdated(readerId);
      toast.success("Card reader updated successfully");
      handleClose();
    } catch (error) {
      logger.exception("reader_update_failed", error, { readerId });
      toast.error(
        error instanceof Error ? error.message : "Failed to update card reader"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Card Reader</DialogTitle>
          <DialogDescription>
            Update the label used to identify this card reader in Plasma POS.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="label">Card Reader Label</Label>
              <Input
                id="label"
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Front Counter"
                value={label}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              disabled={isSubmitting}
              onClick={handleClose}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit">
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2 size-4" />
                  Updating...
                </>
              ) : (
                "Update Card Reader"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
